import React from 'react';
import { Tab, Tabs } from '@mui/material';

/**
 * The three ways of reading a marked quiz back. "All Questions" leads, since it
 * is the whole quiz; the other two are what the learner came to check.
 *
 * Each carries what to say when nothing matches it — for "Incorrect" that is
 * not an empty list but a perfect score, and it should read like one.
 */
export const RESULT_FILTERS = [
  { value: 'all', label: 'All Questions', empty: 'This quiz has no questions.' },
  { value: 'correct', label: 'Correct', empty: 'You did not answer any question correctly.' },
  { value: 'incorrect', label: 'Incorrect', empty: 'You answered every question correctly!' },
];

/** Whether a marked question belongs under the filter on screen. */
export const matchesFilter = (result, filter) => {
  if (filter === 'correct') return result.isCorrect;
  if (filter === 'incorrect') return !result.isCorrect;
  return true;
};

/**
 * Whether a marked quiz has anything to tell apart. A quiz answered all one way
 * does not: on a perfect score "Correct" is the whole quiz and "Incorrect" is
 * none of it, so the tabs would only offer to hide everything.
 * @param {Array<Object>} results Output of gradeQuiz, one entry per question
 * @returns {Boolean} Whether both a right and a wrong answer are among them
 */
export const isResultsFilterable = (results) =>
  results.some(({ isCorrect }) => isCorrect) && results.some(({ isCorrect }) => !isCorrect);

/**
 * The tabs that narrow a marked quiz to what went right or wrong. Both readings
 * of the results use them — the phone's list of questions and the wide screen's
 * marked cards — so the two offer the same three choices in the same order.
 *
 * Props:
 *   id       (string)   - ID prefix for generated element IDs.
 *   filter   (string)   - Which of RESULT_FILTERS is on screen.
 *   onChange (function) - Called with the filter to switch to.
 */
export const ResultsFilterTabs = ({ id, filter, onChange }) => (
  <Tabs
    className="resultsTabs"
    id={`${id}__tabs_results`}
    value={filter}
    onChange={(_event, next) => onChange(next)}
    aria-label="Filter questions"
  >
    {RESULT_FILTERS.map(({ value, label }) => (
      <Tab
        key={value}
        className="resultsTab"
        id={`${id}__tab_${value}`}
        value={value}
        label={label}
      />
    ))}
  </Tabs>
);
